import React from 'react';
import { TimelineItem, ActiveFunctions, FunctionType } from '@/types/item';
import { gateColors, type GateType } from '@/constants/gateColors';

interface ItemsFunctionColumnProps {
  item: TimelineItem;
  functionType: FunctionType;
  activeFunctions: ActiveFunctions;
  activeGates: {
    g1: boolean;
    g2: boolean;
    g3: boolean;
    g4: boolean;
    g5: boolean;
  };
  onClick: () => void;
}

const ItemsFunctionColumn = ({ item, functionType, activeFunctions, activeGates, onClick }: ItemsFunctionColumnProps) => {
  // Only render if this function is active
  if (!activeFunctions[functionType]) {
    return (
      <button 
        onClick={onClick}
        className="w-full h-6 bg-transparent rounded-sm opacity-50"
        title={`${functionType.toUpperCase()} (disabled)`}
      >
        <div className="w-full h-full bg-muted/20 rounded-sm"></div>
      </button>
    );
  }

  // Calculate gate data for this specific function
  const calculateFunctionGates = () => {
    const gates = ['g1', 'g2', 'g3', 'g4', 'g5'] as const;
    const functionData = item.timelineData[functionType];
    
    if (!functionData || typeof functionData !== 'object') {
      return { segments: [], totalCoverage: 0 };
    }

    // Filter gates based on activeGates filter
    const activeGatesList = gates.filter(gate => activeGates[gate]);
    
    const segments = activeGatesList.map(gate => {
      const gateData = functionData[gate as keyof typeof functionData];
      let value = 0;
      
      if (gateData && typeof gateData === 'object' && 'percentage' in gateData) {
        value = (gateData.percentage as number) || 0;
      }
      
      return {
        gate,
        value,
        active: value > 0
      };
    });

    const totalCoverage = segments.reduce((sum, seg) => sum + seg.value, 0);
    return { segments, totalCoverage };
  };

  const { segments, totalCoverage } = calculateFunctionGates();


  return (
    <button 
      onClick={onClick}
      className="w-full h-8 bg-transparent rounded-sm overflow-hidden hover:bg-muted/20 transition-colors p-0"
      title={`${functionType.toUpperCase()}: ${totalCoverage}% total coverage`}
    >
      <div className="flex h-full w-full">
        {segments.map(({ gate, value, active }) => {
          const segmentWidth = segments.length > 0 ? 100 / segments.length : 0; // Dynamic width based on active gates
          const fillPercent = value > 0 ? Math.min(value, 100) : 0;
          
          return (
            <div 
              key={gate}
              className="relative"
              style={{ width: `${segmentWidth}%` }}
            >
              {/* Background - transparent */}
              <div className="w-full h-full bg-transparent border-r border-muted/30 last:border-r-0"></div>
              
              {/* Fill */}
              {active && (
                <div 
                  className="absolute bottom-0 left-0 w-full opacity-70"
                  style={{ 
                    height: `${(fillPercent / 100) * 100}%`,
                    backgroundColor: gateColors[gate as GateType]
                  }}
                ></div>
              )}
            </div>
          );
        })}
      </div>
    </button>
  );
};

export default ItemsFunctionColumn;